import { inject } from '@angular/core';
import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpContextToken,
} from '@angular/common/http';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);
// Import your chosen toast service
import { ToastrService } from 'ngx-toastr';
import { MonitoringService } from '../services/monitoring.service';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const toastr = inject(ToastrService);

  return next(req).pipe(
    catchError((error: any) => {
      if (error instanceof HttpErrorResponse) {
        let errorMessage = 'An unexpected error occurred';
        
        // Extract meaningful error message
        if (error.status === 0) {
          errorMessage = 'Unable to reach the server. Please check your connection and try again.';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        } else {
          switch (error.status) {
            case 400:
              errorMessage = 'Bad request. Please check your input.';
              break;
            case 401:
              errorMessage = 'Authentication failed. Please log in again.';
              break;
            case 403:
              errorMessage = 'Access denied. You don\'t have permission.';
              break;
            case 404:
              errorMessage = 'Resource not found.';
              break;
            case 409:
              errorMessage = 'Conflict. Resource already exists.';
              break;
            case 422:
              errorMessage = 'Validation failed. Please check your data.';
              break;
            case 429:
              errorMessage = 'Too many requests. Please try again later.';
              break;
            case 500:
              errorMessage = 'Internal server error. Please try again.';
              break;
            case 502:
              errorMessage = 'Service temporarily unavailable.';
              break;
            case 503:
              errorMessage = 'Service unavailable. Please try again later.';
              break;
            default:
              errorMessage = error.message || `Error ${error.status}: ${error.statusText}`;
          }
        }

        // Skip showing toast for certain status codes (optional)
        const skipToastForStatuses = [401]; // Don't show toast for auth errors as they redirect

        if (!skipToastForStatuses.includes(error.status) && !req.context.get(SKIP_ERROR_TOAST)) {
          // Show toast notification
          toastr.error(errorMessage, 'Error', {
            timeOut: 5000,
            closeButton: true,
            progressBar: true,
          });
        }

        // Add Sentry breadcrumb and capture unexpected server errors
        MonitoringService.addBreadcrumb('http', 'HTTP error response', {
          status: error.status,
          message: errorMessage,
          url: error.url,
          method: req.method,
        });

        // Capture 5xx and unexpected errors in Sentry
        if (error.status >= 500 || error.status === 0) {
          MonitoringService.captureException(error, {
            url: error.url,
            status: error.status,
            statusText: error.statusText,
            requestMethod: req.method,
          });
        }

        // Log error for debugging
        console.error('HTTP Error:', {
          status: error.status,
          message: errorMessage,
          url: error.url,
          error: error.error
        });
      }

      // Re-throw the error so components can handle it if needed
      return throwError(() => error);
    })
  );
};